import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Filter,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  Archive,
  RefreshCw,
  Save,
  X,
  Zap,
  Target,
  AlertCircle,
  FileText,
  Copy,
  Eye,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Clock },
  draft: { label: 'Draft', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Clock },
  archived: { label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30', icon: Archive },
};

const SIGNAL_CATEGORIES = [
  { value: 'credit_report', label: 'Credit Report Signals' },
  { value: 'collection_notice', label: 'Collection Notice Signals' },
  { value: 'account_status', label: 'Account Status' },
  { value: 'delinquency', label: 'Delinquency Patterns' },
  { value: 'bureau_data', label: 'Bureau Data' },
  { value: 'violation', label: 'Violation Detection' },
];

const SAMPLE_SIGNALS = [
  { id: 'sig_001', name: 'Charge-off Present', category: 'account_status', pattern: 'status == "charge_off"', status: 'active', priority: 1 },
  { id: 'sig_002', name: 'Bankruptcy Present', category: 'account_status', pattern: 'public_records.includes("bankruptcy")', status: 'active', priority: 2 },
  { id: 'sig_003', name: 'Collection Present', category: 'account_status', pattern: 'account_type == "collection"', status: 'active', priority: 3 },
  { id: 'sig_004', name: 'Repossession Present', category: 'account_status', pattern: 'remarks.includes("repossession")', status: 'active', priority: 4 },
  { id: 'sig_005', name: 'Late Payment Count', category: 'delinquency', pattern: 'late_30_count + late_60_count + late_90_count', status: 'active', priority: 5 },
  { id: 'sig_006', name: '1-2 Lates Only', category: 'delinquency', pattern: 'total_late_count <= 2', status: 'active', priority: 6 },
  { id: 'sig_007', name: '3+ Lates', category: 'delinquency', pattern: 'total_late_count >= 3', status: 'active', priority: 7 },
  { id: 'sig_008', name: 'Extended Delinquency', category: 'delinquency', pattern: 'late_90_count > 0 || late_120_count > 0', status: 'active', priority: 8 },
  { id: 'sig_009', name: 'Validation Notice Missing', category: 'collection_notice', pattern: '!validation_notice_present', status: 'active', priority: 9 },
  { id: 'sig_010', name: 'Payment Pressure Language', category: 'collection_notice', pattern: 'contains_pressure_language', status: 'active', priority: 10 },
  { id: 'sig_011', name: 'Cross-Bureau Inconsistency', category: 'bureau_data', pattern: 'bureau_data_differs', status: 'draft', priority: 11 },
  { id: 'sig_012', name: 'Student Loan Majority', category: 'account_status', pattern: 'student_loan_ratio > 0.5', status: 'draft', priority: 12 },
];

const RuleCard = ({ rule, onEdit, onDelete, onDuplicate }) => {
  const [showMenu, setShowMenu] = useState(false);
  const StatusIcon = STATUS_CONFIG[rule.status]?.icon || Clock;
  const category = SIGNAL_CATEGORIES.find(c => c.value === rule.category);

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all group"
      data-testid={`extraction-rule-${rule.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-600/20 flex items-center justify-center">
            <Filter className="text-teal-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{rule.name}</h3>
            <p className="text-xs text-slate-500">{category?.label || rule.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[rule.status]?.color}`}>
            {STATUS_CONFIG[rule.status]?.label || rule.status}
          </span>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-stone-800 transition-all"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-40 bg-stone-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1">
                <button
                  onClick={() => { onEdit(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => { onDuplicate(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Copy size={14} /> Duplicate
                </button>
                <button
                  onClick={() => { onDelete(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <p className="text-xs text-slate-500 mb-1">Pattern:</p>
        <code className="text-xs text-amber-300 bg-stone-800 px-2 py-1 rounded font-mono block overflow-x-auto">
          {rule.pattern}
        </code>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>Priority: {rule.priority}</span>
        <span className="flex items-center gap-1">
          <Target size={12} />
          Signal Extraction
        </span>
      </div>
    </div>
  );
};

const ExtractionRulesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'credit_report',
    pattern: '',
    description: '',
    priority: 1,
    status: 'draft',
  });

  // Fetch rules from API
  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rules-methods/extraction-rules`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch extraction rules:', error);
      // If no rules exist, seed defaults
      if (error.response?.status === 404 || (error.response?.data?.rules || []).length === 0) {
        await seedDefaultRules();
      }
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultRules = async () => {
    try {
      await axios.post(`${API}/rules-methods/seed-default-rules`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      // Refetch after seeding
      const response = await axios.get(`${API}/rules-methods/extraction-rules`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRules(response.data.rules || []);
      toast.success('Default rules loaded');
    } catch (error) {
      console.error('Failed to seed rules:', error);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const filteredRules = rules.filter(rule => {
    if (searchQuery && !rule.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (categoryFilter !== 'all' && rule.category !== categoryFilter) return false;
    if (statusFilter !== 'all' && rule.status !== statusFilter) return false;
    return true;
  });

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.pattern.trim()) {
      toast.error('Name and pattern are required');
      return;
    }

    try {
      if (editingRule) {
        await axios.put(`${API}/rules-methods/extraction-rules/${editingRule.id}`, formData, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        toast.success('Extraction rule updated');
      } else {
        await axios.post(`${API}/rules-methods/extraction-rules`, formData, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        toast.success('Extraction rule created');
      }

      setShowCreateModal(false);
      setEditingRule(null);
      setFormData({ name: '', category: 'credit_report', pattern: '', description: '', priority: 1, status: 'draft' });
      fetchRules();
    } catch (error) {
      toast.error('Failed to save rule');
      console.error(error);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      category: rule.category,
      pattern: rule.pattern,
      description: rule.description || '',
      priority: rule.priority,
      status: rule.status,
    });
    setShowCreateModal(true);
  };

  const handleDuplicate = async (rule) => {
    try {
      await axios.post(`${API}/rules-methods/extraction-rules`, {
        ...rule,
        name: `${rule.name} (Copy)`,
        status: 'draft',
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Rule duplicated');
      fetchRules();
    } catch (error) {
      toast.error('Failed to duplicate rule');
    }
  };

  const handleDelete = async (rule) => {
    try {
      await axios.delete(`${API}/rules-methods/extraction-rules/${rule.id}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  return (
    <div className="space-y-6" data-testid="extraction-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Filter className="text-teal-400" />
            Extraction Rules
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Define what signals to detect and normalize from documents
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-teal-600 hover:bg-teal-700">
          <Plus size={16} className="mr-2" />
          New Rule
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Zap className="text-teal-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-teal-300">Extraction Rules Purpose</h3>
            <p className="text-sm text-teal-200/70 mt-1">
              These rules define <strong>what to detect</strong> from uploaded documents. They extract raw signals like 
              "charge-off present", "late payment count", "validation notice missing" etc. 
              The extracted signals are then passed to <strong>Routing Rules</strong> for classification.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            placeholder="Search rules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-stone-900 border-slate-700 text-white"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px] bg-stone-900 border-slate-700 text-white">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {SIGNAL_CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] bg-stone-900 border-slate-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{rules.length}</p>
          <p className="text-sm text-slate-500">Total Rules</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-400">{rules.filter(r => r.status === 'active').length}</p>
          <p className="text-sm text-slate-500">Active</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-400">{rules.filter(r => r.status === 'draft').length}</p>
          <p className="text-sm text-slate-500">Draft</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-400">{SIGNAL_CATEGORIES.length}</p>
          <p className="text-sm text-slate-500">Categories</p>
        </div>
      </div>

      {/* Rules Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-12 bg-stone-900 rounded-xl border border-stone-800">
          <AlertCircle size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white">No rules found</h3>
          <p className="text-slate-400">Try adjusting your filters or create a new rule</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRules.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          setEditingRule(null);
          setFormData({ name: '', category: 'credit_report', pattern: '', description: '', priority: 1, status: 'draft' });
        }
      }}>
        <DialogContent className="bg-stone-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Extraction Rule' : 'Create Extraction Rule'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Define a signal extraction pattern for document parsing
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Rule Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Charge-off Present"
                className="bg-stone-800 border-slate-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Category</label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="bg-stone-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIGNAL_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Priority</label>
                <Input
                  type="number"
                  min={1}
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                  className="bg-stone-800 border-slate-700"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Pattern Expression</label>
              <Textarea
                value={formData.pattern}
                onChange={(e) => setFormData(prev => ({ ...prev, pattern: e.target.value }))}
                placeholder='e.g., status == "charge_off"'
                className="bg-stone-800 border-slate-700 font-mono text-sm"
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Description (optional)</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What this rule detects..."
                className="bg-stone-800 border-slate-700"
                rows={2}
              />
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-1 block">Status</label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="bg-stone-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700">
              <Save size={16} className="mr-2" />
              {editingRule ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExtractionRulesPage;
